/**
 * Whether the sources behind a cell agree — one component, used everywhere.
 *
 * REQ-WEB-006 (amended 2026-09-10) asks two different questions of a cell and
 * the API answers both (REQ-API-020): `competing` is how many sources spoke,
 * `distinct_values` is how many different answers they gave. Only the second
 * is a disagreement.
 *
 * This exists as one component rather than six inline ternaries because the
 * marker had six sites — three in results.tsx, two in detail.tsx, one in
 * provenance.tsx — and label fixes in this repository have twice covered one
 * surface and been reported as covering all of them. A seventh caller gets the
 * right behaviour by construction.
 *
 * Corroboration is rendered as a positive mark rather than as nothing at all.
 * On 2026-09-10, 1,191 cells were independent publishers reporting the same
 * figure and every one carried a warning triangle. Those are the best-evidenced
 * cells in the corpus; suppressing the warning without saying so would throw
 * the signal away a second time.
 */

export type Agreement = "single" | "corroborated" | "disagreement";

export function agreementOf(
  competing: number,
  distinct: number,
  publishers: number,
  agree: boolean,
): Agreement {
  // REQ-DM-024: values that differ only by how far each source rounded are not
  // a disagreement. 102 of 138 queued conflicts were exactly that — Nikon's
  // "0.34x" against Wikipedia's computed "0.3448x".
  if (!agree) return "disagreement";
  if (distinct > 1) return "single";
  // Independence, not page count (REQ-API-021). One publisher's catalogue feed
  // and the same publisher's product page are two citations and one piece of
  // evidence, which is 527 cells in the corpus.
  if (competing > 1 && publishers > 1) return "corroborated";
  return "single";
}

/** The inline marker beside a value. Renders nothing for a single source. */
export function AgreementMark(props: {
  competing: number | undefined;
  distinct: number | undefined;
  publishers?: number | undefined;
  agree?: boolean | undefined;
}) {
  const competing = props.competing ?? 0;
  // A cell that predates `distinct_values`, or one the API omitted it from,
  // must not silently become "everything agrees" — fall back to the count so
  // the old, noisier behaviour is what a missing field produces.
  const distinct = props.distinct ?? competing;
  // Same fallback reasoning: an absent count must not invent independence.
  const publishers = props.publishers ?? competing;
  // An API that has not sent the field yet must not invent agreement, so fall
  // back to the older, noisier test.
  const agree = props.agree ?? distinct <= 1;
  // No hover text (#213): a phone has no hover, so the meaning is a line in the
  // page footer (LegendNote) and, once a value is tapped, in its pop-up. The
  // accessible name stays, for a screen reader.
  switch (agreementOf(competing, distinct, publishers, agree)) {
    case "disagreement":
      return (
        <span
          class="spec-conflict"
          role="img"
          aria-label={`${competing} sources, ${distinct} different values`}
        >
          ⚠
        </span>
      );
    case "corroborated":
      return (
        <span class="spec-corroborated" role="img" aria-label={`${publishers} publishers agree`}>
          ✓
        </span>
      );
    default:
      return null;
  }
}

/**
 * What the two marks mean, once per page (#213, owner 2026-10-03). The meaning
 * used to be hover-only. The list, lens and comparison footers all use this, so
 * the three cannot word it differently.
 */
/** The key to the table's marks. `dash` adds the empty-cell mark, for the list,
 *  whose footer has no other line explaining it. */
export function LegendNote(props: { dash?: boolean }) {
  return (
    <p class="dfoot legend-note">
      {props.dash ? "— no value cited yet · " : ""}⚠ sources disagree · ✓ two publishers agree. Tap a value to see them.
    </p>
  );
}
